import { organizationsId, type OrganizationsId } from "./Organizations";
import { usersId, type UsersId } from "./Users";
import { localeEnum, type default as LocaleEnum } from "./LocaleEnum";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for projects */
export type ProjectsId = string & { __flavor: "ProjectsId" };

/**
 * Represents the table public.projects
 * @uniqueConstraints {"projects_pkey":{"name":"projects_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"project_org_slug_unique":{"name":"project_org_slug_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"organization_id","definition":"organization_id","predicate":null},{"name":"slug","definition":"slug","predicate":null}]},"project_org_name_unique":{"name":"project_org_name_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"organization_id","definition":"organization_id","predicate":null},{"name":"name","definition":"name","predicate":null}]}}
 */
export default interface ProjectsTable {
  id: ColumnType<ProjectsId, ProjectsId | undefined, ProjectsId>;

  name: ColumnType<string, string, string>;

  description: ColumnType<string | null, string | null, string | null>;

  organizationId: ColumnType<OrganizationsId, OrganizationsId, OrganizationsId>;

  creatorId: ColumnType<UsersId | null, UsersId | null, UsersId | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>;

  color: ColumnType<string, string, string>;

  slug: ColumnType<string, string, string>;

  defaultLocales: ColumnType<LocaleEnum[], LocaleEnum[] | undefined, LocaleEnum[]>;
}

export type Projects = Selectable<ProjectsTable>;

export type NewProjects = Insertable<ProjectsTable>;

export type ProjectsUpdate = Updateable<ProjectsTable>;

export const projectsId = z.string() as unknown as z.Schema<ProjectsId>;

export const projects = z.object({
  id: projectsId,
  name: z.string(),
  description: z.string().nullable(),
  organizationId: organizationsId,
  creatorId: usersId.nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  color: z.string(),
  slug: z.string(),
  defaultLocales: localeEnum,
});

export const projectsInitializer = z.object({
  id: projectsId.optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  organizationId: organizationsId,
  creatorId: usersId.optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  color: z.string(),
  slug: z.string(),
  defaultLocales: localeEnum.optional(),
});

export const projectsMutator = z.object({
  id: projectsId.optional(),
  name: z.string().optional(),
  description: z.string().optional().nullable(),
  organizationId: organizationsId.optional(),
  creatorId: usersId.optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  color: z.string().optional(),
  slug: z.string().optional(),
  defaultLocales: localeEnum.optional(),
});

// ── constraints for Projects ─────────────────────────────────
export const ProjectsConstraints = {
  projects_pkey: {
    name: "projects_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  project_org_slug_unique: {
    name: "project_org_slug_unique",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "organization_id", definition: "organization_id", predicate: null },
      { name: "slug", definition: "slug", predicate: null },
    ],
  },
  project_org_name_unique: {
    name: "project_org_name_unique",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "organization_id", definition: "organization_id", predicate: null },
      { name: "name", definition: "name", predicate: null },
    ],
  },
} as const;
export type ProjectsConstraintsType = typeof ProjectsConstraints;
